/**
 * RunBlock — one network RUN rendered directly (NO wrapper/collapsible chrome).
 *
 * The same network runs many times in a room; each run takes a DIFFERENT route (the Lead engages a
 * different subset), and the topology itself can change across a MAJOR release. So each run renders
 * its own turns + its own live agent tree — on THAT run's topology (its ``releaseVersion``), never
 * re-projected onto the current one. Old runs stay intact after an upgrade.
 *
 * Per-run topology: fetches the manifest for its ``releaseVersion`` via ``useNetworkAsl`` (version-
 * keyed cache), seeds the stable roster from it, and merges the run's live roster over it
 * (mergeRoster) — identical to the room's current-run logic, just scoped to this run.
 */
import React, { useMemo, useState } from 'react';
import { useNetworkAsl } from '../../hooks/useNetworkAsl.js';
import { manifestToRoster, isManifest } from '@stepflow/ui-shared/utils/manifestToReactFlow.js';
import { mergeRoster } from '@stepflow/ui-shared/utils/agentTree.js';
import TurnGroup from './TurnGroup.jsx';
import AgentHierarchyPanel from './AgentHierarchyPanel.jsx';

/**
 * @param {Object} props
 * @param {string} props.runId
 * @param {import('@/utils/agentRoomReducer.js').TurnGroup[]} props.turns  turns of THIS run (ordered)
 * @param {Array<Object>} props.liveRoster   the run's live agentRoster slice (from agentRostersByRun)
 * @param {number|null} props.releaseVersion  the topology version THIS run executed on
 * @param {boolean} props.isLiveRun          true for the current/latest run
 * @param {boolean} props.defaultOpen        expand on mount (the live/most-recent run)
 * @param {string} props.networkId
 * @param {string} props.poolId
 * @param {number} [props.stageDepthCap]  frozen manual stage cap (0=Auto); dims agents deeper than N
 * @param {number} props.index               1-based run number (newest = highest)
 * @param {Object} props.builderControls     shared multi-builder controls (Stop/awaiting/band)
 * @param {Set<string>} props.handledBuilderWaitSet
 * @param {Set<string>} props.resolvedWaitEventIds
 * @param {(row: Object) => React.ReactNode} props.renderRowAction
 */
export default function RunBlock({
  turns, liveRoster, releaseVersion, isLiveRun,
  networkId, poolId, stageDepthCap = 0, stageCost = null, builderControls,
  handledBuilderWaitSet, resolvedWaitEventIds, renderRowAction,
  // CONTROLLED selection (live run only): shared with the docked tree so clicking a tree node
  // loads that agent into this run's shared container — and switching to another live builder
  // shows THAT builder's stream. Historical runs leave these undefined → local selection.
  selectedAgentId: controlledSelectedAgentId, onSelectAgent,
  // FOCUS (multi-run disambiguation): with several run blocks stacked and each carrying its OWN
  // graph, the user needs a visual anchor for "which execution's graph am I looking at". ``focused``
  // highlights THIS block; clicking anywhere in it calls ``onFocus(runId)``. When more than one run
  // is on screen the parent passes these; a lone run leaves them undefined (no highlight noise).
  focused = false, onFocus, runId, verbose = false,
}) {
  const [localSelectedAgentId, setLocalSelectedAgentId] = useState(null);
  const controlled = typeof onSelectAgent === 'function';
  const selectedAgentId = controlled ? controlledSelectedAgentId : localSelectedAgentId;
  const setSelectedAgentId = controlled ? onSelectAgent : setLocalSelectedAgentId;

  // Per-run topology: fetch THIS run's release manifest (version-keyed cache). Falls back to the
  // room's latest when the run carries no version (unpinned/legacy).
  const { asl } = useNetworkAsl(networkId, releaseVersion || undefined);
  const seeded = useMemo(() => (isManifest(asl) ? manifestToRoster(asl) : []), [asl]);

  // ROSTER FALLBACK from the run's own turns. ``agentRostersByRun[rid]`` is only keyed when the
  // run's TOP-LEVEL EXECUTION_STARTED was seen; for a HISTORICAL run whose start fell outside the
  // loaded event window, that slice is empty EVEN THOUGH its turns carry the agent finals. Rebuild
  // a ``{agentId, status:'done', text, usage}`` roster from those finals so the tree still renders
  // its real (engaged) agents — merged over the seeded topology for tree position. (This is what
  // regressed to individual per-agent cards: an empty ``liveRoster`` hid the tree.)
  const rosterFromTurns = useMemo(() => {
    const byId = new Map();
    for (const t of (turns || [])) {
      for (const e of (Array.isArray(t?.agentEntries) ? t.agentEntries : [])) {
        const id = String(e?.agentName || '').trim();
        if (!id || id === 'Agent' || id === 'Summary' || byId.has(id)) continue;
        byId.set(id, { agentId: id, label: id, status: 'done', text: e.text, usage: e.usage });
      }
    }
    return Array.from(byId.values());
  }, [turns]);
  // The effective live roster: the reducer's per-run slice when present, else rebuilt from turns.
  const effectiveRoster = (Array.isArray(liveRoster) && liveRoster.length > 0)
    ? liveRoster : rosterFromTurns;

  // Run is settled when it's not the live run and no turn is still live → a queued seeded agent
  // was NOT selected (gray it out).
  const runSettled = !isLiveRun && !turns.some((t) => t.status === 'live');
  const merged = useMemo(
    () => (seeded.length
      ? mergeRoster(seeded, effectiveRoster, { runSettled, stageDepthCap })
      : (effectiveRoster || [])),
    [seeded, effectiveRoster, runSettled, stageDepthCap],
  );
  // A run has REAL roster activity when at least one agent advanced past ``queued`` (it ran /
  // is running / awaiting / done / failed). ``merged`` is ALWAYS ≥ the seeded manifest size, so
  // ``merged.length`` alone is NOT a valid "has activity" test — a run with NO engaged agent would
  // otherwise fabricate a phantom "0/13, all not required" tree. We now check the EFFECTIVE roster
  // (reducer slice OR rebuilt-from-turns), so a historical run with finals correctly shows its tree.
  const hasActivity = useMemo(
    () => (Array.isArray(effectiveRoster) ? effectiveRoster.some((r) => r && r.status && r.status !== 'queued') : false),
    [effectiveRoster],
  );
  // Show the tree for the LIVE run always (the stable pre-seeded topology, all ``standby`` before
  // agents report in — the intended live behavior); for a SETTLED/historical run only when it
  // actually had activity (else it's the phantom "0/13 not required" block described above).
  const hasTree = (isLiveRun || hasActivity) && merged.length > 0;

  // The run's task/execution id — what the worker window's "Context" (PromptCaptureLink) needs as
  // ``task`` (worker capture key = pool/agent/task). Same value the old per-turn panel got as
  // ``turn.executionId``. Pulled off the run's turns (any turn of this run carries it).
  const runTaskId = turns.find((t) => t.executionId)?.executionId
    || turns.find((t) => t.runId)?.runId
    || null;

  // Has the FINAL SUMMARY landed? The final reducer publishes it as an ``AGENT_FINAL_RESPONSE``
  // with agentName "Summary" — the deliverable. It arrives ~40s AFTER the last worker (the reducer
  // synthesis runs post-frontier). ``merged`` is the same roster the panel splits the Summary out
  // of, so we detect it the same way (agentId === 'summary').
  const summaryArrived = useMemo(
    () => (Array.isArray(merged) ? merged.some(
      (r) => r && String(r.agentId || '').trim().toLowerCase() === 'summary'
        && typeof r.text === 'string' && r.text.length > 0,
    ) : false),
    [merged],
  );
  // Did the run FAIL? The reducer marks the live turn ``failed`` on a TOP-LEVEL EXECUTION_FAILED
  // (e.g. the FrontierMap aborting on a builder error). A failed run NEVER produces a Summary, so
  // without this the spinner would run FOREVER (the exact "orion stuck running, agents done" bug):
  // late in-flight child finals kept the live turn alive and no Summary ever arrived to stop it.
  // We check ``failed`` ONLY — NOT ``completed`` — because a SUCCESSFUL run's turn flips to
  // ``completed`` ~40s BEFORE its Summary lands (the reducer synthesis runs post-frontier), and
  // stopping on ``completed`` would kill the spinner before the deliverable shows (the bug the
  // Summary-gate fixed). Success is handled by ``summaryArrived``; failure by this.
  const runFailed = useMemo(
    () => (Array.isArray(turns) ? turns.some((t) => t && t.status === 'failed') : false),
    [turns],
  );
  // SPINNER GATE: keep "Network running" alive on the live run until the SUMMARY shows (success) OR
  // the run FAILS. NOT gated on uiState==='RUNNING' — each worker sub-execution emits its own
  // UI_STATE=IDLE as it finishes, so uiState flips ~40-90s before the Summary lands. Keeps the user
  // active through the synthesis gap, but never past a real end (success = Summary, failure = fail).
  const runLive = isLiveRun && !summaryArrived && !runFailed;

  // PHANTOM-BLOCK GUARD: a run block with NO tree AND no turn that renders anything (no user
  // bubble, no agent output, no builder surface) is content-free — a bare lifecycle grouping (e.g.
  // a stray EXECUTION_STARTED whose agent events fell outside the loaded window). Rendering it
  // produces the empty "Network run / 0-of-13 not required" block the user saw. Skip it entirely.
  const anyTurnHasContent = turns.some((t) => (
    (t && t.userEntry)
    || (Array.isArray(t?.agentEntries) && t.agentEntries.length > 0)
    || (Array.isArray(t?.builderTerminalLines) && t.builderTerminalLines.length > 0)
    || (Array.isArray(t?.builderPhases) && t.builderPhases.length > 0)
  ));
  if (!hasTree && !anyTurnHasContent) return null;

  // FOCUS affordance: when the parent manages focus (multiple runs on screen), the FOCUS highlight
  // lives on the "Network run" PANEL's own border (darkened via the ``focused`` prop below) — NOT a
  // new wrapper box around the whole run (that double-boxed the turns + panel). Here we only dim the
  // unfocused run + make it click-to-focus; no border/padding of our own. Lone run → bare.
  const focusManaged = typeof onFocus === 'function';
  const shellClass = !focusManaged
    ? 'space-y-3'
    : `space-y-3 transition-opacity cursor-default ${focused ? '' : 'opacity-60 hover:opacity-90'}`;
  return (
    <div
      className={shellClass}
      data-testid="run-block"
      data-focused={focused ? '1' : '0'}
      onClick={focusManaged && !focused ? () => onFocus(runId) : undefined}
    >
      {turns.map((turn) => (
        <TurnGroup
          key={turn.id}
          turn={turn}
          networkId={networkId}
          poolId={poolId}
          handledBuilderWaitSet={handledBuilderWaitSet}
          resolvedWaitEventIds={resolvedWaitEventIds}
          // The tree owns the WHOLE agent surface for this run (workers AND builders — its shared
          // reader renders the live builder terminal / done transcript off the roster row). So hide
          // the turn-card agent stack + inline builder here; the turn keeps only the user bubble +
          // awaiting band. Without this the builder double-showed (inline PTY + clicked reader block).
          hideAgentStack={hasTree}
        />
      ))}
      {hasTree ? (
        <AgentHierarchyPanel
          roster={merged}
          networkId={networkId}
          poolId={poolId}
          taskId={runTaskId}
          stageCost={stageCost}
          focused={focusManaged ? focused : undefined}
          verbose={verbose}
          selectedAgentId={selectedAgentId}
          onAgentSelect={setSelectedAgentId}
          live={runLive}
          failed={runFailed}
          headerRight={
            isLiveRun && builderControls.awaitingCount > 0 ? (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                {builderControls.awaitingCount} awaiting your response
              </span>
            ) : null
          }
          renderRowAction={isLiveRun ? renderRowAction : undefined}
        />
      ) : null}
    </div>
  );
}
